import React, { useState } from 'react';
import { Booking } from '../../types';
import { ConicGlowCard } from '../common/ConicGlowCard';
import {
  DownloadIcon,
  ShareIcon,
  QrCodeIcon,
  CalendarIcon,
  ClockIcon,
  CheckIcon,
  ExternalLinkIcon,
  RadioDotIcon,
  ArrowLeftIcon
} from '../common/Icons';

interface BookingTrackerDeckProps {
  bookingData?: any;
  onSendReplyToOwner?: (bookingCode: string, message: string) => Promise<void>;
  onBack?: () => void;
  onTouchAction?: (action: string, data?: any) => void;
}

export const BookingTrackerDeck: React.FC<BookingTrackerDeckProps> = ({
  bookingData,
  onSendReplyToOwner,
  onBack,
  onTouchAction
}) => {
  const [replyText, setReplyText] = useState('');
  const [isSendingReply, setIsSendingReply] = useState(false);
  const [selectedVisitSlot, setSelectedVisitSlot] = useState<string>('Tomorrow • 6:30 PM');

  const defaultBooking: Booking = {
    id: 'b-default-1',
    sessionId: 'sess-01',
    venueId: 'v-karsaz-01',
    venueName: 'Majestic Grand Banquet',
    venueArea: 'Karsaz',
    venueAddress: 'Main Karsaz Road, Karachi',
    bookingCode: 'MHF-882194',
    eventType: 'barat',
    eventDate: '2026-11-25',
    slot: 'dinner',
    guestCount: 400,
    estimatedTotalPkr: 740000,
    holdExpiresAt: new Date(Date.now() + 71 * 3600 * 1000).toISOString(),
    status: 'provisional_hold',
    createdAt: new Date().toISOString(),
    inquiryMessages: [
      {
        id: 'm1',
        bookingId: 'b-default-1',
        sessionId: 'sess-01',
        venueId: 'v-karsaz-01',
        senderRole: 'owner',
        senderName: 'Tariq Mehmood (General Manager)',
        messageText: 'Assalam-o-Alaikum! Your 72-hour provisional hold for 25 Nov (Dinner Shift) is locked on our main register. Would you like to visit tomorrow at 6:30 PM for a hall inspection, generator backup check, and stage walkthrough?',
        counterQuotePkr: null,
        createdAt: new Date().toISOString()
      }
    ]
  };

  const raw = bookingData?.booking || bookingData?.bookings?.[0] || bookingData || {};
  const fallbackPkgPrice =
    bookingData?.selectedPackage?.pricePkr ||
    bookingData?.venue?.packages?.find((p: any) => p.packageType?.includes('flat_rental'))?.pricePkr ||
    0;
  const fallbackTotal =
    bookingData?.draft?.totalEstimatedPkr ||
    (fallbackPkgPrice >= 50000 ? fallbackPkgPrice + 45000 : 0);
  const currentBooking: Booking = {
    ...defaultBooking,
    ...raw,
    bookingCode: raw.bookingCode || defaultBooking.bookingCode,
    venueName:
      raw.venueName ||
      bookingData?.draft?.venueName ||
      bookingData?.venue?.name ||
      defaultBooking.venueName,
    estimatedTotalPkr:
      raw.estimatedTotalPkr || fallbackTotal || defaultBooking.estimatedTotalPkr,
    holdExpiresAt: raw.holdExpiresAt || defaultBooking.holdExpiresAt,
    inquiryMessages: raw.inquiryMessages || defaultBooking.inquiryMessages
  };

  const expiresDate = new Date(currentBooking.holdExpiresAt);
  const now = new Date();
  const hoursRemaining = Math.max(1, Math.round((expiresDate.getTime() - now.getTime()) / (1000 * 3600)));

  const rawVenuePhone = String(
    raw.venueWhatsapp ||
      raw.venuePhone ||
      bookingData?.venue?.whatsapp ||
      bookingData?.venue?.phone ||
      ''
  ).replace(/[^0-9]/g, '');
  const normalizedWaPhone = rawVenuePhone
    ? rawVenuePhone.startsWith('0')
      ? `92${rawVenuePhone.slice(1)}`
      : rawVenuePhone
    : '';
  const waUrl = normalizedWaPhone
    ? `https://wa.me/${normalizedWaPhone}?text=Salam,%20I%20have%20an%20official%20Mehfil%20provisional%20hold%20${currentBooking.bookingCode}%20for%20${encodeURIComponent(currentBooking.venueName || 'Majestic Grand Banquet')}`
    : null;

  const handleDownloadPdf = () => {
    if (onTouchAction) {
      onTouchAction('download_voucher_pdf', { bookingCode: currentBooking.bookingCode });
    }
    window.open(`/api/v1/bookings/${encodeURIComponent(currentBooking.bookingCode)}/voucher.pdf`, '_blank');
  };

  const handleSendReply = async (customText?: string) => {
    const textToSend = (customText ?? replyText).trim();
    if (!textToSend || !onSendReplyToOwner) return;
    try {
      setIsSendingReply(true);
      await onSendReplyToOwner(currentBooking.bookingCode, textToSend);
      if (!customText) setReplyText('');
    } catch (e) {
      console.error('Failed to send reply:', e);
    } finally {
      setIsSendingReply(false);
    }
  };

  const visitSlots = [
    'Today • 8 PM',
    'Tomorrow • 6:30 PM',
    'Sat • 5 PM'
  ];

  return (
    <div className="stage-deck-content">
      <div className="deck-header" style={{ marginBottom: '0.38rem', alignItems: 'center', flexWrap: 'nowrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', minWidth: 0, flex: '1 1 auto' }}>
          {onBack && (
            <button
              type="button"
              className="btn-secondary"
              onClick={onBack}
              style={{ padding: '0.26rem 0.52rem', fontSize: '0.68rem', gap: '0.24rem', flexShrink: 0 }}
            >
              <ArrowLeftIcon size={12} />
              <span>Back</span>
            </button>
          )}
          <h2 className="deck-title" style={{ margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            Hold Voucher
          </h2>
        </div>

        <div style={{ display: 'flex', gap: '0.32rem', flexWrap: 'nowrap', flexShrink: 0 }}>
          <button
            id="download-pdf-voucher"
            type="button"
            className="btn-primary"
            style={{ padding: '0.28rem 0.58rem', fontSize: '0.7rem', gap: '0.25rem' }}
            onClick={handleDownloadPdf}
          >
            <DownloadIcon size={12} />
            <span>
              PDF<span className="header-mobile-hide"> Voucher</span>
            </span>
          </button>

          {waUrl && (
            <a
              href={waUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary"
              style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '0.24rem', padding: '0.28rem 0.54rem', fontSize: '0.7rem' }}
            >
              <ShareIcon size={12} />
              <span>WhatsApp</span>
            </a>
          )}
        </div>
      </div>

      {/* 4-Step Booking Lifecycle Status Bar */}
      <div className="glass-panel" style={{ padding: '0.3rem 0.42rem', marginBottom: '0.42rem', display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: '0.26rem' }}>
        {[
          { step: 'Quote', status: 'Done', done: true },
          { step: '72h Hold', status: 'Locked', done: true },
          { step: 'Visit', status: 'Pending', done: false },
          { step: 'Advance', status: 'On Visit', done: false }
        ].map((item, idx) => (
          <div
            key={idx}
            style={{
              padding: '0.24rem 0.3rem',
              borderRadius: '0.48rem',
              backgroundColor: item.done ? 'hsl(var(--accent-emerald) / 0.12)' : 'hsl(var(--surface-elevated) / 0.55)',
              border: item.done ? '1px solid hsl(var(--accent-emerald) / 0.4)' : '1px solid hsl(var(--surface-border) / 0.5)',
              display: 'flex',
              alignItems: 'center',
              gap: '0.24rem',
              minWidth: 0
            }}
          >
            <span
              style={{
                width: '0.88rem',
                height: '0.88rem',
                borderRadius: '50%',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: item.done ? 'hsl(var(--accent-emerald))' : 'hsl(var(--surface-border))',
                color: 'hsl(var(--obsidian-950))',
                fontSize: '0.55rem',
                fontWeight: 800,
                flexShrink: 0
              }}
            >
              {item.done ? <CheckIcon size={8} /> : idx + 1}
            </span>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: '0.62rem', fontWeight: 700, color: 'hsl(var(--text-primary))', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {item.step}
              </div>
              <div style={{ fontSize: '0.54rem', color: item.done ? 'hsl(var(--accent-emerald))' : 'hsl(var(--text-muted))', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {item.status}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(19rem, 100%), 1fr))', gap: '0.45rem' }}>
        {/* Left: Visual Certificate Card & Site Visit Scheduler */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.45rem' }}>
          <ConicGlowCard glowIntensity={0.8}>
            <div style={{ padding: '0.62rem 0.78rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.45rem', marginBottom: '0.42rem' }}>
                <div>
                  <span style={{ fontSize: '0.56rem', color: 'hsl(var(--text-muted))', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700 }}>
                    Official Hold Reference
                  </span>
                  <div
                    className="num-tabular"
                    style={{
                      fontSize: '1.08rem',
                      fontWeight: 800,
                      color: 'hsl(var(--accent-gold))',
                      letterSpacing: '0.02em',
                      marginTop: '0.02rem',
                      fontFamily: 'var(--font-display)'
                    }}
                  >
                    #{currentBooking.bookingCode}
                  </div>
                </div>

                <div
                  style={{
                    padding: '0.22rem 0.5rem',
                    borderRadius: '0.5rem',
                    backgroundColor: 'hsl(var(--accent-coral) / 0.14)',
                    border: '1px solid hsl(var(--accent-coral) / 0.4)',
                    textAlign: 'center'
                  }}
                >
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.18rem', fontSize: '0.54rem', color: 'hsl(var(--accent-coral))', fontWeight: 700 }}>
                    <ClockIcon size={9} /> Hold Lock
                  </span>
                  <span className="num-tabular" style={{ fontSize: '0.76rem', fontWeight: 800, color: 'hsl(var(--text-primary))' }}>
                    {hoursRemaining}h left
                  </span>
                </div>
              </div>

              {/* Certificate Detail Rows */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.7rem', borderTop: '1px solid hsl(var(--surface-border) / 0.5)', paddingTop: '0.38rem', marginBottom: '0.44rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.4rem' }}>
                  <span style={{ color: 'hsl(var(--text-secondary))', flexShrink: 0 }}>Banquet Venue:</span>
                  <span style={{ fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{currentBooking.venueName}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.4rem' }}>
                  <span style={{ color: 'hsl(var(--text-secondary))', flexShrink: 0 }}>Date &amp; Shift:</span>
                  <span style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{currentBooking.eventDate} ({currentBooking.slot.toUpperCase()}) • {currentBooking.guestCount} Pax</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.4rem' }}>
                  <span style={{ color: 'hsl(var(--text-secondary))', flexShrink: 0 }}>Provisional Total:</span>
                  <span className="num-tabular" style={{ fontWeight: 800, color: 'hsl(var(--accent-gold))', whiteSpace: 'nowrap' }}>
                    Rs. {Number(currentBooking.estimatedTotalPkr).toLocaleString()} PKR
                  </span>
                </div>
                {currentBooking.confirmedTotalPkr && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.4rem' }}>
                    <span style={{ color: 'hsl(var(--accent-emerald))', flexShrink: 0 }}>Counter-Quote:</span>
                    <span className="num-tabular" style={{ fontWeight: 800, color: 'hsl(var(--accent-emerald))', whiteSpace: 'nowrap' }}>
                      Rs. {Number(currentBooking.confirmedTotalPkr).toLocaleString()} PKR
                    </span>
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '0.24rem', borderTop: '1px dashed hsl(var(--surface-border) / 0.6)' }}>
                  <span style={{ color: 'hsl(var(--text-secondary))', display: 'inline-flex', alignItems: 'center', gap: '0.22rem' }}>
                    <QrCodeIcon size={11} /> Verify URL:
                  </span>
                  <a
                    href={`/verify/${currentBooking.bookingCode}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: 'hsl(var(--glow-gemini-blue))', textDecoration: 'none', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: '0.18rem' }}
                  >
                    <span>/verify/{currentBooking.bookingCode}</span>
                    <ExternalLinkIcon size={10} />
                  </a>
                </div>
              </div>

              {/* Site Visit Quick Scheduler — 3-Column Grid (Zero Clipping) */}
              <div style={{ padding: '0.38rem 0.5rem', borderRadius: '0.5rem', backgroundColor: 'hsl(var(--surface-elevated) / 0.65)', border: '1px solid hsl(var(--surface-border) / 0.5)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.24rem', gap: '0.25rem' }}>
                  <span style={{ fontSize: '0.64rem', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: '0.22rem', color: 'hsl(var(--text-primary))' }}>
                    <CalendarIcon size={11} /> Schedule Walkthrough
                  </span>
                  <span style={{ fontSize: '0.56rem', color: 'hsl(var(--accent-emerald))', fontWeight: 600 }}>
                    Stage &amp; Backup Check
                  </span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '0.22rem' }}>
                  {visitSlots.map((slotLabel) => (
                    <button
                      key={slotLabel}
                      type="button"
                      className={`corridor-chip ${selectedVisitSlot === slotLabel ? 'active' : ''}`}
                      style={{ fontSize: '0.58rem', padding: '0.18rem 0.14rem', justifyContent: 'center', width: '100%' }}
                      onClick={() => {
                        setSelectedVisitSlot(slotLabel);
                        handleSendReply(`Salam, we would like to schedule our hall walkthrough and stage inspection for ${slotLabel}.`);
                      }}
                    >
                      <span>{slotLabel}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </ConicGlowCard>
        </div>

        {/* Right: Two-Way Hall Owner Message Bridge */}
        <div className="glass-panel" style={{ padding: '0.62rem 0.78rem', display: 'flex', flexDirection: 'column', height: '100%' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.3rem' }}>
            <h3 style={{ fontSize: '0.82rem', fontWeight: 800, margin: 0 }}>
              Direct Hall Management Bridge
            </h3>
            <span className="pill-badge verified" style={{ fontSize: '0.56rem', padding: '0.1rem 0.38rem', display: 'inline-flex', alignItems: 'center', gap: '0.18rem' }}>
              <RadioDotIcon size={8} />
              <span>Live Sync</span>
            </span>
          </div>

          {/* Messages list */}
          <div
            style={{
              flex: 1,
              minHeight: '3.6rem',
              maxHeight: '6.8rem',
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: '0.35rem',
              marginBottom: '0.38rem',
              paddingRight: '0.15rem'
            }}
          >
            {currentBooking.inquiryMessages && currentBooking.inquiryMessages.length > 0 ? (
              currentBooking.inquiryMessages.map((msg) => (
                <div
                  key={msg.id}
                  style={{
                    padding: '0.4rem 0.56rem',
                    borderRadius: '0.55rem',
                    backgroundColor: msg.senderRole === 'owner' ? 'hsl(var(--surface-elevated))' : 'hsl(var(--glow-gemini-blue) / 0.16)',
                    border: '1px solid hsl(var(--surface-border) / 0.65)',
                    alignSelf: msg.senderRole === 'owner' ? 'flex-start' : 'flex-end',
                    maxWidth: '96%'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.1rem', fontSize: '0.62rem' }}>
                    <span style={{ fontWeight: 700, color: msg.senderRole === 'owner' ? 'hsl(var(--accent-gold))' : 'hsl(var(--glow-gemini-blue))' }}>
                      {msg.senderName || (msg.senderRole === 'owner' ? 'Banquet General Manager' : 'You')}
                    </span>
                    <span style={{ color: 'hsl(var(--text-muted))', marginLeft: '0.4rem' }}>
                      {new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>

                  <p style={{ fontSize: '0.68rem', color: 'hsl(var(--text-primary))', lineHeight: 1.28, margin: 0 }}>
                    {msg.messageText}
                  </p>

                  {msg.counterQuotePkr && (
                    <div
                      style={{
                        marginTop: '0.2rem',
                        padding: '0.18rem 0.4rem',
                        borderRadius: '0.38rem',
                        backgroundColor: 'hsl(var(--accent-emerald) / 0.18)',
                        border: '1px solid hsl(var(--accent-emerald) / 0.4)',
                        color: 'hsl(var(--accent-emerald))',
                        fontWeight: 800,
                        fontSize: '0.64rem'
                      }}
                    >
                      Official Counter-Quote: Rs. {Number(msg.counterQuotePkr).toLocaleString()} PKR
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div style={{ textAlign: 'center', color: 'hsl(var(--text-muted))', padding: '0.6rem 0', fontSize: '0.7rem' }}>
                No messages yet. The banquet general manager has been alerted of your hold.
              </div>
            )}
          </div>

          {/* Quick Reply Chips — 3-Column Grid (Zero Clipping) */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '0.22rem', marginBottom: '0.36rem' }}>
            {[
              'Visit tomorrow 6:30?',
              'Generator fuel incl.?',
              'Valet & AC capacity?'
            ].map((quickMsg) => (
              <button
                key={quickMsg}
                type="button"
                className="corridor-chip"
                style={{ fontSize: '0.57rem', padding: '0.18rem 0.12rem', justifyContent: 'center', width: '100%' }}
                onClick={() => handleSendReply(quickMsg)}
              >
                <span>{quickMsg}</span>
              </button>
            ))}
          </div>

          {/* Reply Box */}
          <div style={{ display: 'flex', gap: '0.35rem' }}>
            <input
              type="text"
              placeholder="Message hall manager..."
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSendReply();
              }}
              className="form-input"
              style={{ padding: '0.32rem 0.52rem', fontSize: '0.72rem' }}
            />
            <button
              type="button"
              className="btn-primary"
              disabled={isSendingReply || !replyText.trim()}
              onClick={() => handleSendReply()}
              style={{ whiteSpace: 'nowrap', padding: '0.32rem 0.68rem', fontSize: '0.72rem' }}
            >
              <span>{isSendingReply ? 'Sending...' : 'Send'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
